import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Accordion } from "react-bootstrap";
import {
    getNotes,
    createNote,
    sendNoteNotifications,
    addStudentToWatchlist,
    removeStudentFromWatchlist,
} from "../services/APIClient";
import "./SenWatchlistModal.css";

function SenWatchlistModal({
    show,
    onHide,
    student,
    action, // 'add', 'remove', or 'note'
    teacherid,
    onSuccess,
    teachers, // List of all teachers for filtering SENCO/SET
}) {
    const [note, setNote] = useState("");
    const [existingNotes, setExistingNotes] = useState([]);
    const [sendEmail, setSendEmail] = useState(false);
    const [selectedTeachers, setSelectedTeachers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetchingNotes, setFetchingNotes] = useState(false);

    // Filter SENCO/SET teachers
    const sencoSetTeachers = teachers.filter(
        (teacher) =>
            teacher.additionalRoles &&
            (teacher.additionalRoles.includes("SENCO") ||
                teacher.additionalRoles.includes("SET") ||
                teacher.additionalRoles.includes("senco") ||
                teacher.additionalRoles.includes("set"))
    );

    useEffect(() => {
        if (show && student) {
            fetchNotes();
        }
    }, [show, student]);

    useEffect(() => {
        if (!show) {
            setNote("");
            setSendEmail(false);
            setSelectedTeachers([]);
            setExistingNotes([]);
        }
    }, [show]);

    const fetchNotes = async () => {
        if (action === "add") {
            try {
                const response = await getNotes('student', student.id, 'SenWatchlist');
                setExistingNotes(response.data || []);
            } catch {
                setExistingNotes([]);
            }
            return;
        }

        setFetchingNotes(true);
        try {
            const response = await getNotes('student', student.id, 'SenWatchlist');
            setExistingNotes(response.data || []);
        } catch (error) {
            console.error("Error fetching notes:", error);
            setExistingNotes([]);
        } finally {
            setFetchingNotes(false);
        }
    };

    const formatIrelandTime = (timestamp) => {
        const date = new Date(timestamp);
        return date.toLocaleString("en-IE", {
            day: "numeric",
            month: "short",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
            timeZone: "Europe/Dublin",
        });
    };

    const getNoteLabel = () => {
        if (action === "add") return "Why are you adding this student?";
        if (action === "remove") return "Why are you removing this student?";
        return "Leave a note";
    };

    const getModalTitle = () => {
        if (action === "add") {
            const isFirstTime = !existingNotes || existingNotes.length === 0;
            return isFirstTime ? "Add Student to SEN Watchlist" : "Re-add Student to SEN Watchlist";
        }
        if (action === "remove") return "Remove Student from SEN Watchlist";
        return "Add Note to SEN Watchlist";
    };


    const handleTeacherSelection = (teacherId) => {
        setSelectedTeachers((prev) =>
            prev.includes(teacherId)
                ? prev.filter((id) => id !== teacherId)
                : [...prev, teacherId]
        );
    };

    const handleSubmit = async () => {
        if (!note.trim()) {
            alert("Please provide a note before submitting.");
            return;
        }

        if (sendEmail && selectedTeachers.length === 0) {
            alert("Please select at least one teacher to notify.");
            return;
        }

        setLoading(true);

        try {
            // Perform watchlist action (add/remove)
            // note is passed to satisfy the backend endpoint requirement
            if (action === "add") {
                await addStudentToWatchlist({ studentid: student.id, teacherid, note: note.trim() });
            } else if (action === "remove") {
                await removeStudentFromWatchlist(student.id, { teacherid, note: note.trim() });
            }

            // Store note in generic notes system (non-blocking — toggle succeeds regardless)
            let noteId;
            try {
                const createdNote = await createNote({
                    elementtype: 'student',
                    elementid: student.id,
                    note_type: 'SenWatchlist',
                    type: 'general',
                    note_text: note.trim(),
                    teacherid,
                });
                noteId = createdNote.data?.id;
            } catch (noteError) {
                console.error("Failed to save note to generic notes system:", noteError);
            }

            if (sendEmail && noteId) {
                await sendNoteNotifications(selectedTeachers, noteId);
            }

            onSuccess?.();
            onHide();
        } catch (error) {
            console.error("Error submitting watchlist action:", error);
            alert("Failed to complete the action. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        if (note.trim()) {
            const confirmed = window.confirm(
                "You have unsaved changes. Are you sure you want to cancel? This will discard your note and no changes will be made to the watchlist."
            );
            if (!confirmed) return;
        }
        onHide();
    };

    const getTeacherName = (tid) => {
        const teacher = teachers.find((t) => t.id === tid);
        return teacher?.name || "Unknown";
    };

    const getTeacherRoles = (teacher) => {
        if (!teacher.additionalRoles || teacher.additionalRoles.length === 0) return "";
        const senRoles = teacher.additionalRoles
            .filter(role => {
                const r = role.toLowerCase();
                return r === "senco" || r === "set" || r === "special educator/set";
            })
            .map(role => role.toUpperCase());
        return [...new Set(senRoles)].join(", ");
    };

    const getBadgeLabel = (type) => {
        if (type === "initial") return "Initial";
        if (type === "removal") return "Removal";
        return "SEN Watchlist";
    };

    return (
        <Modal show={show} onHide={handleCancel} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{getModalTitle()}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <h5>{student?.name}</h5>

                {/* Existing Notes Accordion */}
                {existingNotes.length > 0 && (
                    <Accordion className="mb-3">
                        <Accordion.Item eventKey="0">
                            <Accordion.Header>
                                <strong>View Previous Notes ({existingNotes.length})</strong>
                            </Accordion.Header>
                            <Accordion.Body>
                                {fetchingNotes ? (
                                    <p>Loading notes...</p>
                                ) : (
                                    <div className="notes-list">
                                        {existingNotes.map((n) => (
                                            <div key={n.id} className="note-item mb-3">
                                                <div className="note-header">
                                                    <span className="note-type-badge">
                                                        {getBadgeLabel(n.type)}
                                                    </span>
                                                    <span className="note-meta">
                                                        {getTeacherName(n.teacherid)} •{" "}
                                                        {formatIrelandTime(n.date_created)}
                                                    </span>
                                                </div>
                                                <div className="note-content">{n.note_text}</div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                )}

                {/* Note Input */}
                <Form.Group className="mb-3">
                    <Form.Label>
                        <strong>{getNoteLabel()}</strong>{" "}
                        <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={4}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Enter your note here..."
                        required
                    />
                </Form.Group>

                {/* Email Notification Checkbox */}
                <Form.Group className="mb-3">
                    <Form.Check
                        type="checkbox"
                        label="Send email notification"
                        checked={sendEmail}
                        onChange={(e) => setSendEmail(e.target.checked)}
                    />
                </Form.Group>

                {/* Teacher Selection */}
                {sendEmail && (
                    <Form.Group className="mb-3">
                        <Form.Label>
                            <strong>Select SEN teachers to notify:</strong>
                        </Form.Label>
                        {sencoSetTeachers.length === 0 ? (
                            <p className="text-muted">
                                No SENCO or SET teachers available for notification.
                            </p>
                        ) : (
                            <div className="teacher-selection">
                                {sencoSetTeachers.map((teacher) => (
                                    <Form.Check
                                        key={teacher.id}
                                        type="checkbox"
                                        label={`${teacher.name}${getTeacherRoles(teacher) ? ` (${getTeacherRoles(teacher)})` : ""}`}
                                        checked={selectedTeachers.includes(teacher.id)}
                                        onChange={() => handleTeacherSelection(teacher.id)}
                                    />
                                ))}
                            </div>
                        )}
                    </Form.Group>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={handleCancel} disabled={loading}>
                    Cancel
                </Button>
                <button
                    className="submit-button"
                    onClick={handleSubmit}
                    disabled={loading}
                >
                    {loading ? "Submitting..." : "Submit"}
                </button>
            </Modal.Footer>
        </Modal>
    );
}

export default SenWatchlistModal;
